/**
 * Service consuming the Core::Catalog benef API (/api/benef/catalog/*).
 */
import { USER_API_ENDPOINTS } from "@/features/auth/config/userApiPath";
import { RENTALS_API_ENDPOINTS } from "@/features/rentals/configs/rentalsApiPath.config";
import type {
  AvailableCheckInDatesResponse,
  AvailableCheckOutDatesResponse,
  CheckoutCartPayload,
  CheckoutCartResponse,
  CreateBookingPayload,
  CreateVoyageBookingPayload,
  GetRentalBookingResponse,
  GetRentalBookingsResponse,
  GetRentalCartResponse,
  GetRentalCollectionResponse,
  GetRentalCollectionsResponse,
  GetRentalItemResponse,
  GetRentalItemsResponse,
  GetSeasonQuotaUsageResponse,
  InitiatePaymentResponse,
  RentalAccountStatementResponse,
  RentalCategory,
  RentalItemsFilters,
  RentalPaymentMethod,
} from "@/features/rentals/types/rentals.type";
import { fetcher } from "@/shared/lib/fetcher.client";

export const getRentalCollections = async (): Promise<GetRentalCollectionsResponse> => {
  const response = await fetcher.get<GetRentalCollectionsResponse>(
    RENTALS_API_ENDPOINTS.GET_COLLECTIONS,
  );
  return response.data;
};

export const getRentalCollection = async (
  collectionId: string,
): Promise<GetRentalCollectionResponse> => {
  const response = await fetcher.get<GetRentalCollectionResponse>(
    RENTALS_API_ENDPOINTS.GET_COLLECTION.replace(":collectionId", collectionId),
  );
  return response.data;
};

export const getRentalItems = async (
  filters?: RentalItemsFilters,
): Promise<GetRentalItemsResponse> => {
  const params: Record<string, string | string[]> = {};
  if (filters?.search) params["q[name_cont]"] = filters.search;
  if (filters?.collection_id) {
    params["q[catalog_collection_id_eq]"] = filters.collection_id;
  }
  if (filters?.category_ids?.length) {
    // `categories_prefix_id_in` est un scope ransackable côté API
    // (cf. `Core::Catalog::Item`) qui décode les `CCA_…` en IDs
    // numériques avant le JOIN — sinon ransack tente
    // `WHERE catalog_categories.id IN ('CCA_…')` et matche zéro ligne.
    // /!\ pas de `[]` dans la clé : axios l'ajoute automatiquement
    // sur les valeurs array (sinon `[][]` et Ransack 500).
    params["q[categories_prefix_id_in]"] = filters.category_ids;
  }
  if (filters?.page) params.page = String(filters.page);
  if (filters?.limit) params.limit = String(filters.limit);
  const response = await fetcher.get<GetRentalItemsResponse>(
    RENTALS_API_ENDPOINTS.GET_ITEMS,
    { params: Object.keys(params).length ? params : undefined },
  );
  return response.data;
};

export const getRentalItem = async (
  itemId: string,
): Promise<GetRentalItemResponse> => {
  const response = await fetcher.get<GetRentalItemResponse>(
    RENTALS_API_ENDPOINTS.GET_ITEM.replace(":itemId", itemId),
  );
  return response.data;
};

export const getSeasonQuotaUsage = async (
  itemId: string,
): Promise<GetSeasonQuotaUsageResponse> => {
  const response = await fetcher.get<GetSeasonQuotaUsageResponse>(
    RENTALS_API_ENDPOINTS.GET_SEASON_QUOTA_USAGE.replace(":itemId", itemId),
  );
  return response.data;
};

export const getRentalCategoriesForCollection = async (
  collectionId: string,
): Promise<RentalCategory[]> => {
  const response = await fetcher.get<{ categories: RentalCategory[] }>(
    RENTALS_API_ENDPOINTS.GET_CATEGORIES_FOR_COLLECTION.replace(
      ":collectionId",
      collectionId,
    ),
  );
  return response.data.categories;
};

// Two-step datepicker pour `kind: hebergement` : on liste d'abord les
// arrivées proposées par l'élu (sans param), puis les départs pour
// l'arrivée choisie. Sépare les 2 calls pour que React Query puisse
// cacher chaque branche indépendamment (l'utilisateur peut changer
// d'arrivée sans refetch des dates d'arrivée).
export const getAvailableCheckInDates = async (
  itemId: string,
): Promise<AvailableCheckInDatesResponse> => {
  const response = await fetcher.get<AvailableCheckInDatesResponse>(
    RENTALS_API_ENDPOINTS.GET_AVAILABLE_DATES.replace(":itemId", itemId),
  );
  return response.data;
};

export const getAvailableCheckOutDates = async (
  itemId: string,
  checkInDate: string,
): Promise<AvailableCheckOutDatesResponse> => {
  const response = await fetcher.get<AvailableCheckOutDatesResponse>(
    RENTALS_API_ENDPOINTS.GET_AVAILABLE_DATES.replace(":itemId", itemId),
    { params: { check_in_date: checkInDate } },
  );
  return response.data;
};

// Cart unifié — Phase 1+. Le cart est source de vérité pour la page
// panier et le checkout multi-bookings. Un seul cart `open` par
// bénéf à la fois (mirror deals).
export const getCurrentRentalCart = async (): Promise<GetRentalCartResponse> => {
  const response = await fetcher.get<GetRentalCartResponse>(
    RENTALS_API_ENDPOINTS.GET_CART_CURRENT,
  );
  return response.data;
};

// Attache (ou détache) l'adresse de livraison choisie à l'étape
// « Vos coordonnées » au cart unifié. À la confirmation cart-level
// (`checkout`), le backend snapshot cette adresse sur chaque booking
// — donc l'envoi DOIT précéder le `checkoutRentalCart`. Iso mirror
// du flow Reducce (`wf_carts.adresse_livraison_id`).
export const updateRentalCartShippingAddress = async (
  cartId: string,
  shippingAddressId: number | null,
): Promise<GetRentalCartResponse> => {
  const response = await fetcher.patch<GetRentalCartResponse>(
    RENTALS_API_ENDPOINTS.UPDATE_CART.replace(":cartId", cartId),
    { cart: { shipping_address_id: shippingAddressId } },
  );
  return response.data;
};

export const checkoutRentalCart = async (
  cartId: string,
  payload: CheckoutCartPayload,
): Promise<CheckoutCartResponse> => {
  const response = await fetcher.post<CheckoutCartResponse>(
    RENTALS_API_ENDPOINTS.CHECKOUT_CART.replace(":cartId", cartId),
    payload,
  );
  return response.data;
};

export const emptyRentalCart = async (
  cartId: string,
): Promise<GetRentalCartResponse> => {
  const response = await fetcher.post<GetRentalCartResponse>(
    RENTALS_API_ENDPOINTS.EMPTY_CART.replace(":cartId", cartId),
  );
  return response.data;
};

export const updateRentalCartSubsidyChoices = async (
  cartId: string,
  choices: { employee?: boolean, beneficiaries?: Record<string, boolean> },
): Promise<GetRentalCartResponse> => {
  const response = await fetcher.patch<GetRentalCartResponse>(
    RENTALS_API_ENDPOINTS.UPDATE_CART_SUBSIDY_CHOICES.replace(":cartId", cartId),
    { subsidy_choices: choices },
  );
  return response.data;
};

export const getMyRentalBookings = async (): Promise<GetRentalBookingsResponse> => {
  const response = await fetcher.get<GetRentalBookingsResponse>(
    RENTALS_API_ENDPOINTS.GET_BOOKINGS,
  );
  return response.data;
};

export const getRentalBooking = async (
  bookingId: string,
): Promise<GetRentalBookingResponse> => {
  const response = await fetcher.get<GetRentalBookingResponse>(
    RENTALS_API_ENDPOINTS.GET_BOOKING.replace(":bookingId", bookingId),
  );
  return response.data;
};

export const createRentalBooking = async (
  payload: CreateBookingPayload | CreateVoyageBookingPayload,
): Promise<GetRentalBookingResponse> => {
  const response = await fetcher.post<GetRentalBookingResponse>(
    RENTALS_API_ENDPOINTS.CREATE_BOOKING,
    payload,
  );
  return response.data;
};

export const cancelRentalBooking = async (
  bookingId: string,
  comment?: string,
): Promise<GetRentalBookingResponse> => {
  const response = await fetcher.post<GetRentalBookingResponse>(
    RENTALS_API_ENDPOINTS.CANCEL_BOOKING.replace(":bookingId", bookingId),
    comment ? { comment } : undefined,
  );
  return response.data;
};

export const initiateRentalBookingPayment = async (
  bookingId: string,
  paymentMethod: RentalPaymentMethod = "credit_card",
  shippingAddressId?: number | null,
): Promise<InitiatePaymentResponse> => {
  const response = await fetcher.post<InitiatePaymentResponse>(
    RENTALS_API_ENDPOINTS.INITIATE_PAYMENT.replace(":bookingId", bookingId),
    {
      payment_method: paymentMethod,
      // Per-booking checkout : si l'utilisateur a choisi une adresse
      // à l'étape `RentalAddressStep`, le backend la persiste sur le
      // booking. Le cart-level checkout fait son propre snapshot.
      ...(shippingAddressId ? { shipping_address_id: shippingAddressId } : {}),
    },
  );
  return response.data;
};

export const getRentalAccountStatement = async (): Promise<RentalAccountStatementResponse> => {
  const response = await fetcher.get<RentalAccountStatementResponse>(
    RENTALS_API_ENDPOINTS.ACCOUNT_STATEMENT,
  );
  return response.data;
};

export const updateRentalSubsidyChoices = async (
  bookingId: string,
  choices: { employee?: boolean, beneficiaries?: Record<string, boolean> },
): Promise<GetRentalBookingResponse> => {
  const response = await fetcher.patch<GetRentalBookingResponse>(
    RENTALS_API_ENDPOINTS.UPDATE_SUBSIDY_CHOICES.replace(":bookingId", bookingId),
    { subsidy_choices: choices },
  );
  return response.data;
};

/** Enregistre la seule valeur d'un attribut personnalisé du profil (la
 * tranche d'un voyage), sans renvoyer le reste du profil : un autre champ
 * invalide (téléphone, champ obligatoire) ne doit pas bloquer la saisie. */
export const saveEmployeeCustomAttributeValue = async (
  attributeId: number,
  valueId: number,
): Promise<void> => {
  await fetcher.put(USER_API_ENDPOINTS.ME, { employee: { custom_attributes: [{ attribute_id: attributeId, value_id: valueId }] } });
};
